import React from 'react';
import type { RelatedTaskSummary, Task } from '../../../../types';
import type { Note } from '@/types/notes';
import { TaskGeneralInfo } from '../../panel/TaskGeneralInfo';
import { GeometryView } from '../../panel/GeometryView';
import { TaskRequirements } from '../../panel/TaskRequirements';
import { TaskComments } from '../../panel/TaskComments';
import { TaskAttachments } from '../../panel/TaskAttachments';
import { TaskHistory } from '../../panel/TaskHistory';
import { TaskRelatedTasks } from '../../panel/TaskRelatedTasks';
import {
  TaskLinkedNotes,
  type NonNavigableNote,
} from '../../panel/TaskLinkedNotes';
import type { NoteNavigationPayload } from '../hooks/useTaskDetailsPanelNavigation';

// Define the tab type locally since it's specific to this component
export type TabType =
  | 'general'
  | 'mapRepresentation'
  | 'requirements'
  | 'comments'
  | 'attachments'
  | 'history'
  | 'relatedTasks'
  | 'notes';

interface TaskDetailsContentProps {
  activeTab: TabType;
  task: Task;
  onViewRelatedTask?: (taskId: string, title: string) => void;
  onViewNote?: (note: NoteNavigationPayload) => void;
  setActiveTab: (tab: TabType) => void;
  readOnly?: boolean;
  shareToken?: string;
  /** Public share: notes supplied externally instead of the auth fetch. */
  providedNotes?: Note[];
  /** Public share: linked notes outside the share — non-clickable rows. */
  nonNavigableNotes?: NonNavigableNote[];
  /** Public share: related tasks outside the share render non-clickable. */
  isTaskNavigable?: (relatedTask: RelatedTaskSummary) => boolean;
}

export const TaskDetailsContent: React.FC<TaskDetailsContentProps> = ({
  activeTab,
  task,
  onViewRelatedTask,
  onViewNote,
  setActiveTab,
  readOnly = false,
  shareToken,
  providedNotes,
  nonNavigableNotes,
  isTaskNavigable,
}) => {
  // Calculate data availability for conditional rendering
  const hasAttachments = task.attachments && task.attachments.length > 0;
  const hasRequirements = task.requirements && task.requirements.length > 0;
  const hasRelatedTasks = task.relatedTasks && task.relatedTasks.length > 0;

  switch (activeTab) {
    case 'general':
      return (
        <TaskGeneralInfo
          task={task}
          onTabChange={setActiveTab}
          readOnly={readOnly}
        />
      );
    case 'mapRepresentation':
      return <GeometryView task={task} />;
    case 'requirements':
      return hasRequirements ? (
        <TaskRequirements task={task} readOnly={readOnly} />
      ) : null;
    case 'comments':
      return <TaskComments task={task} readOnly={readOnly} />;
    case 'attachments':
      return hasAttachments ? (
        <TaskAttachments task={task} shareToken={shareToken} />
      ) : null;
    case 'history':
      return <TaskHistory task={task} />;
    case 'relatedTasks':
      return hasRelatedTasks ? (
        <TaskRelatedTasks
          task={task}
          onViewTask={onViewRelatedTask}
          isTaskNavigable={isTaskNavigable}
        />
      ) : null;
    case 'notes':
      return (
        <TaskLinkedNotes
          taskId={task.id}
          onViewNote={onViewNote}
          providedNotes={providedNotes}
          nonNavigableNotes={nonNavigableNotes}
        />
      );
    default:
      return (
        <TaskGeneralInfo
          task={task}
          onTabChange={setActiveTab}
          readOnly={readOnly}
        />
      );
  }
};
